/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useRef, ReactElement, useCallback, Fragment } from 'react';
import styled from '@emotion/styled';
import { getPatternModal } from '../../../style';
import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import TextLink from '../../common/TextLink';
import PriceInput, { adjustStylePerDigit } from './PriceInput';
import OfferPriceBtnSet from './OfferPriceBtnSet';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import { addCommas, IsMobileViewPort } from '../../../utils/helpers';
import {
  sendOffer,
  sendCancel,
  sendSuggest,
  sendAmountCustom,
  counterOffer,
  counterCancel,
} from '../../../actions/profileTrackAction';
import isEmpty from 'lodash/isEmpty';
import WYPButtonIcon from '../../../atoms/icons/WYPButtonIcon';
import useOnClickTriggerTracking from '../../../hooks/useOnclickTriggerTracking';
import { PriceContainer, getInputProps, CurrencyLabelSpan, CurrencyTip, status } from './common';
import { getDefaultOfferAmount } from '../../../utils/offers';
import { displayWarnMessage } from '../../../actions/notificationActions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { clearErrors } from '../../../actions/commonActions';
import { defaultOfferAmount as commonDefaultOfferAmount } from '../../../common';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import { mobileContentStyle, mobileOverlayStyle } from 'components/common/CommonStyle';
import OfferModalFooter from './OfferModalFooter';
import breakpoints from 'theme/material/breakpoints';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',
  marginTop: 42,

  [breakpoints.up(640)]: {
    marginTop: 12,
  },
});

const CenterDiv = styled('div')({
  textAlign: 'center',
  margin: '20px 0',
});

const AvatarTitleDiv = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  margin: '0 auto',
});

const TitleDiv = styled('span')(() => ({
  color: getPatternModal().titleColor,
  fontSize: 32,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 8,
  [breakpoints.down(479.05)]: {
    fontSize: 27,
  },
}));

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: '1',
  justifyContent: 'space-between',
});

const colorType = (price, hasErrors) => {
  switch (price) {
    case true:
      return 'default';
    case false:
      return !hasErrors ? 'brand' : 'alert';
    default:
      return;
  }
};

const ErrorContainer = styled('div')({
  textAlign: 'center',
  marginTop: '20px',
});

const ErrorMessage = styled('div')({
  color: 'rgb(204, 84, 84)',
  margin: '16px auto 0',
  maxWidth: '280px',
});

const PurchaseCredits = styled('div')({
  textAlign: 'center',
  marginTop: '12px',
  textDecoration: 'underline',
});

const ButtonDiv = styled('div')({
  marginTop: 20,
  [breakpoints.down(639.05)]: {
    marginBottom: 0,
    paddingTop: 12,
    width: '100%',
  },
});

const OfferAmounts = [50, 75, 100, 150, 200];

const getHeadline = (currentUserIsGenerous: boolean, username: string) => {
  return currentUserIsGenerous ? (
    <span>
      Send <strong>{username}</strong> a first date incentive!
    </span>
  ) : (
    <span>
      Request a first date incentive from <strong>{username}</strong>!
    </span>
  );
};

const offerMinMaxVal = (offer, type, defaultAmount) => {
  const offerVal = offer && type === 'min' ? offer.data.min_counter_price : offer && offer.data.max_counter_price;

  return offer ? offerVal : defaultAmount;
};

const validateTextInput = e => {
  const Key = e.key;
  const regex = /[0-9]/;

  if (!regex.test(Key)) {
    if (e.preventDefault) e.preventDefault();
  }
};

const setCreateOfferAction = (_e: Event, offerAction: string) => {
  switch (offerAction) {
    case 'Send Counter':
      counterOffer();
      break;
    case 'Cancel Counter':
      counterCancel();
      break;
    case 'Send Offer':
      sendOffer();
      break;
    case 'Cancel Offer':
      sendCancel();
      break;
    case 'Suggest Offer':
      sendSuggest();
      break;
    default:
      return;
  }
};

const trackFocus = (ref, actionMethod) => {
  if (ref && ref.current) {
    ref.current.addEventListener('click', e => actionMethod(e), false);
  }

  return () => {
    ref.current.removeEventListener('click', () => null, false);
  };
};

interface Props {
  isModalOpen: boolean;
  avatarSrc: any;
  offer?: any;
  closeModal: any;
  currencyLabel: any;
  currencyInitial: any;
  currentUserIsGenerous: any;
  sex: any;
  username: any;
  sendSuggestion: any;
  cannot_make_offer_reasons: any;
  createNewOffer: any;
  hashId: any;
  buttonID: any;
  successOpen: any;
  isSuggestRequest: any;
  title: string;
  pageTitle: string;
  onOfferSuccess?: () => void;
  defaultOfferAmount?: number | string | null;
  customContentStyle?: any;
}

const CreateNewOffer = ({
  isModalOpen,
  avatarSrc,
  offer = '',
  closeModal,
  currencyLabel,
  currencyInitial,
  currentUserIsGenerous,
  sex,
  username,
  sendSuggestion,
  cannot_make_offer_reasons,
  createNewOffer,
  hashId,
  buttonID,
  successOpen,
  isSuggestRequest,
  title = 'Request A Date!',
  pageTitle,
  onOfferSuccess,
  defaultOfferAmount,
  customContentStyle,
}: Props): ReactElement => {
  const dispatch = useDispatch();
  const errors = useSelector((state: RootState) => state.common.errors);
  const defaultOfferPrice = getDefaultOfferAmount();
  const defaultMinOffer = commonDefaultOfferAmount.minValue;
  const [defaultValue] = useState('');
  const [offerPrice, setOfferPrice] = useState(defaultOfferAmount ?? defaultOfferPrice);
  const [minValue, setMinValue] = useState(offerMinMaxVal(offer, 'min', defaultMinOffer));
  const [maxValue, setMaxValue] = useState(offerMinMaxVal(offer, 'max', commonDefaultOfferAmount.maxValue));
  const [hasErrors, setHasErrors] = useState(false);
  const [isOfferSending, setOfferSending] = useState(false);
  const sendOfferBtn = useRef(null);
  const sendCancelBtn = useRef(null);
  const sendSuggestLink = useRef(null);
  const priceInput = useRef(null);
  const { showMandatoryWall } = useMandatoryWall();
  const { isMobile } = useMediaBreakpoints();

  useOnClickTriggerTracking('Send Offer', sendOfferBtn, setCreateOfferAction);
  useOnClickTriggerTracking('Cancel Offer', sendCancelBtn, setCreateOfferAction);
  useOnClickTriggerTracking('Suggest Offer', sendSuggestLink, setCreateOfferAction);

  const handleClearErrors = useCallback(() => {
    dispatch(clearErrors());
  }, [dispatch]);

  useEffect(() => {
    handleClearErrors();
  }, [handleClearErrors]);

  useEffect(() => {
    trackFocus(priceInput, sendAmountCustom);
  }, []);

  useEffect(() => {
    setMaxValue(offerMinMaxVal(offer, 'max', '500'));
    setMinValue(offerMinMaxVal(offer, 'min', defaultMinOffer));
  }, [offer]);

  const updateOfferPrice = (val: string, error: boolean) => {
    if (!error) {
      handleClearErrors();
    }
    setOfferPrice(val);
    setHasErrors(error || false);
  };

  const inputOnChange = e => {
    const InputVal = e.target.value;
    const MaxLength = maxValue.toString().length;

    if (InputVal.length <= MaxLength) {
      const Error = Boolean(Number(InputVal) < Number(minValue) || Number(InputVal) > Number(maxValue));

      updateOfferPrice(InputVal, Error);
    } else {
      e.preventDefault();
    }
  };

  const currLabel = String.fromCharCode(currencyLabel);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = { price: offerPrice, account: hashId, source: pageTitle };

    setOfferSending(true);
    showMandatoryWall(
      () => {
        createNewOffer(formData, offerPrice)
          .then(() => {
            closeModal();
            setOfferSending(false);
            onOfferSuccess?.();
          })
          .catch(() => {
            setOfferSending(false);
            setHasErrors(true);
          });
      },
      () => setOfferSending(false),

      'Make Offer'
    );
  };

  const onHandleSuggestion = e => {
    e.preventDefault();
    // check to see if user can make an offer before allowing it.
    setOfferSending(true);
    showMandatoryWall(
      () => {
        if (cannot_make_offer_reasons === null) {
          // process suggestion

          sendSuggestion(hashId)
            .then(() => {
              closeModal();
              successOpen(true, '', false, false, true);
              onOfferSuccess?.();
            })
            .catch(() => {
              setOfferSending(false);
              setHasErrors(true);
            });
        } else {
          // explain suggestion fail reason.
          dispatch(
            displayWarnMessage({
              info: `You cannot send an offer suggestion: ${cannot_make_offer_reasons[0]}`,
            })
          );
          setOfferSending(false);
        }
      },
      () => setOfferSending(false),
      'Make Offer'
    );
  };

  const formatSubmitButtonText = () => {
    return isOfferSending ? `Sending ${status(currentUserIsGenerous)}...` : `Send ${status(currentUserIsGenerous)}`;
  };

  return (
    <ModalWithPattern
      isModalOpen={isModalOpen}
      dataTestID="new-offer"
      closeBtnClassName="new-offer-modal-close"
      closeBtnDataTestId="new-offer-modal-close"
      modalContentLabel="New Offer Modal"
      onCloseClick={closeModal}
      closeRequest={closeModal}
      customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
      customContentStyle={IsMobileViewPort ? { ...mobileContentStyle, ...customContentStyle } : customContentStyle}
      withoutBgPattern={false}
      hideCloseBtn={false}
      closeOnOverlayClick={true}
      enableTouchMove={true}
    >
      <Fragment>
        <Form>
          <HeaderDiv>
            <AvatarTitleDiv>
              <Avatar
                data-test-id="offer-modal-avatar"
                src={avatarSrc}
                width={isMobile ? '75px' : '100px'}
                customStyle={{
                  backgroundColor: '#ccc',
                }}
              />
              <TitleDiv>{title}</TitleDiv>
            </AvatarTitleDiv>
            <Headline
              style={{
                color: getPatternModal().headerColor,
                marginTop: 20,
                fontSize: isMobile ? 14 : 16,
              }}
              data-test-id="profile-offer-headline"
              size="SM"
            >
              {getHeadline(currentUserIsGenerous, username)}
            </Headline>
          </HeaderDiv>
          <Content>
            <React.Fragment>
              <PriceContainer
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                hasErrors={hasErrors}
              >
                <CurrencyTip
                  offerPrice={colorType(offerPrice, hasErrors)}
                  style={{
                    paddingLeft: adjustStylePerDigit(offerPrice).paddingLeft,
                  }}
                >
                  <CurrencyLabelSpan>
                    {currencyInitial}
                    {currLabel}
                  </CurrencyLabelSpan>
                  <PriceInput
                    inputRef={priceInput}
                    data-test-id="offer-price-input"
                    hasErrors={hasErrors}
                    value={offerPrice}
                    onChange={inputOnChange}
                    validateTextFn={validateTextInput}
                    placeholder={defaultValue}
                    inputProps={{
                      style: {
                        ...getInputProps().style,
                        marginLeft: adjustStylePerDigit(offerPrice).marginLeft,
                        textAlign: 'left',
                      },
                    }}
                    minWidth={IsMobileViewPort ? 100 : 120}
                  />
                </CurrencyTip>
              </PriceContainer>
              <OfferPriceBtnSet
                prices={OfferAmounts}
                updateOfferPrice={updateOfferPrice}
                currentAmount={offerPrice}
                currLabel={currLabel}
                currInitial={currencyInitial}
                offer={offer}
                isCounter={false}
              />
              <div
                style={{
                  textAlign: 'center',
                  marginTop: IsMobileViewPort ? 10 : 20,
                  fontSize: IsMobileViewPort ? 14 : 16,
                }}
                data-test-id="profile-offer-desc"
              >
                Maximum {currentUserIsGenerous ? 'Offer' : 'Request'} amount is{' '}
                {
                  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                  currencyInitial + currLabel + addCommas(parseInt(maxValue, 10))
                }
              </div>
              {!isEmpty(errors) ? (
                <ErrorContainer>
                  <ErrorMessage>{errors.price}</ErrorMessage>
                  {currentUserIsGenerous && Number(offerPrice) >= 200 && (
                    <PurchaseCredits>
                      <TextLink to="/packages">Purchase credits now.</TextLink>
                    </PurchaseCredits>
                  )}
                </ErrorContainer>
              ) : null}
            </React.Fragment>
            <ButtonDiv
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              marginTop={16}
            >
              <ButtonSet align="center">
                <Button
                  id={buttonID}
                  buttonRef={sendOfferBtn}
                  disabled={hasErrors || isOfferSending}
                  buttonType="error"
                  onClick={handleSubmit}
                  type="submit"
                >
                  <WYPButtonIcon style={{ marginRight: 6 }} />
                  {formatSubmitButtonText()}
                </Button>
              </ButtonSet>
              {!isSuggestRequest && (
                <div>
                  <CenterDiv>
                    <strong>&mdash; OR &mdash;</strong>
                  </CenterDiv>
                  <span>
                    <ButtonSet align="center">
                      <TextLink
                        linkRef={sendSuggestLink}
                        data-test-id="offer-suggest-link"
                        to="/"
                        disabled={hasErrors || isOfferSending}
                        onClick={e => onHandleSuggestion(e)}
                        type="submit"
                        style={{ color: '#E21D4A', textDecoration: 'underline' }}
                      >
                        {' '}
                        Ask {sex === 'Male' ? 'him' : 'her'} to suggest an amount
                      </TextLink>
                    </ButtonSet>
                  </span>
                </div>
              )}
              <ButtonSet align="center">
                <Button
                  buttonType="chromeless"
                  buttonRef={sendCancelBtn}
                  data-test-id={`${buttonID}Cancel`}
                  disabled={isOfferSending}
                  onClick={e => {
                    e.preventDefault();
                    closeModal();
                  }}
                  customStyle={{ color: getPatternModal().linkBlue, fontWeight: 'normal', textDecoration: 'underline' }}
                >
                  Cancel
                </Button>
              </ButtonSet>
            </ButtonDiv>
          </Content>
        </Form>
      </Fragment>
      <OfferModalFooter />
    </ModalWithPattern>
  );
};

export default React.memo(CreateNewOffer);
